import React, { useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { useDrop } from 'react-dnd';
import {
  IInvoiceGroups,
  IInvoiceGroupOverrides,
  ISubscriptions,
} from '@llws/lift-entity-interfaces';
import { SubscriptionCard } from 'src/components/pages/InvoiceGroups/SubscriptionCard';
import { EditableFieldKey } from 'src/components/pages/InvoiceGroups/InvoiceGroupManager';
import DeleteIcon from '@mui/icons-material/Delete';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import { compactArray } from '@jamesgmarks/utilities';
import { TLoadedState } from 'src/types/TLoadedState';

interface InvoiceGroupCardProps {
  group: IInvoiceGroups;
  overrides: IInvoiceGroupOverrides[];
  subscriptions: ISubscriptions[];
  subscriptionLoadState: TLoadedState
  onAssign: (groupId: number, subscriptionId: number) => void;
  onEditField: (groupId: number, fieldKey: EditableFieldKey) => void;
  onDelete: (groupId: number) => void;
  onAddressEdit: (groupId: number) => void;
  onErrorChange: (groupId: number, hasErrors: boolean) => void
}

const getChipsFromString = (fieldValue: string) => {
  const stringArray = fieldValue.split(',').filter((val) => val !== '');
  return (
    <Box display="flex" flexWrap="wrap" gap={1} mt={0.5} mb={0.5}>
      {stringArray.map(option => (
        <Chip
          key={option}
          label={option}
          color="success"
        />
      ))}
    </Box>
  );
};

const FieldChipRow = ({
  label,
  fieldKey,
  value,
  groupId,
  onEdit,
}: {
  label: string;
  fieldKey: EditableFieldKey;
  value: string;
  groupId: number;
  onEdit: (groupId: number, fieldKey: EditableFieldKey) => void;
}) => (
  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
    <Typography fontWeight="bold">{label}</Typography>
    <IconButton
      color="primary"
      onClick={() => onEdit(groupId, fieldKey)}
      size="small"
    >
      <AddBoxIcon fontSize="small" />
    </IconButton>
    {getChipsFromString(value)}
  </Stack>
);

const InvoiceGroupCard: React.FC<InvoiceGroupCardProps> = ({
  group,
  overrides,
  subscriptions,
  subscriptionLoadState,
  onAssign,
  onEditField,
  onDelete,
  onAddressEdit,
  onErrorChange,
}) => {
  const [{ isOver, canDrop }, dropRef] = useDrop(() => ({
    accept: 'SUBSCRIPTION',
    drop: (item: { subscriptionId: number }) => {
      onAssign(group.id!, item.subscriptionId);
    },
    canDrop: (item: { subscriptionId: number }) =>
      !overrides.some((o) => o.subscriptionId === item.subscriptionId),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [group.id, overrides]);

  const assignedSubscriptions = useMemo(() => (
    compactArray(overrides
      .map((o) => subscriptions.find((s) => s.id === o.subscriptionId)))
  ), [overrides, subscriptions]);

  const hasErrors = useMemo(() => compactArray([
    (!group.lineItemFields ? 'Line item fields' : null),
    (!group.groupLinesBy ? 'Group lines by' : null),
    (!group.lineDescriptionFields ? 'Line description fields' : null),
  ]), [group]);

  useEffect(() => {
    if (group.id) {
      onErrorChange(group.id, hasErrors.length > 0);
    }
  }, [hasErrors, group.id, onErrorChange]);

  return (
    <Card
      ref={dropRef}
      variant="outlined"
      sx={{
        width: "60vh",
        minHeight: 200,
        p: 2,
        border: isOver && canDrop ? '2px dashed #4caf50' : '1px solid #ccc',
        bgcolor: isOver ? '#f0fff0' : 'white',
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent={"space-between"} alignItems={'center'}>
          <Stack direction="row" spacing={1} >
            <Typography variant="subtitle1" fontWeight="bold">
              Group #{group.id}
            </Typography>
            {group.invoiceGroupAddressOverrides?.organization &&
              <Tooltip title={
                <div>
                  <div><strong>Organization:</strong> {group.invoiceGroupAddressOverrides.organization}</div>
                  <div><strong>Care of:</strong> {group.invoiceGroupAddressOverrides.careOf}</div>
                  <div><strong>Use Client Address:</strong> {
                    group.invoiceGroupAddressOverrides.useClientAddress ? 'True' : 'False'
                  }</div>
                </ div>
              } arrow>
                <Chip
                  label={
                    group.invoiceGroupAddressOverrides.organization.length > 25
                      ? `${group.invoiceGroupAddressOverrides.organization.slice(0, 25)}...`
                      : group.invoiceGroupAddressOverrides.organization
                  }
                  color="primary"
                  size="small"
                />
              </Tooltip>
            }
          </Stack>
          <Box>
            <Stack direction="row" spacing={1}>
              <IconButton
                color="primary"
                onClick={() => onAddressEdit(group.id!)}
                sx={{
                  transition: 'background 0.2s, color 0.2s',
                  '&:hover': {
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                  },
                }}
              >
                <AddLocationIcon />
              </IconButton>
              <IconButton
                color="error"
                onClick={() => onDelete(group.id!)}
                sx={{
                  transition: 'background 0.2s, color 0.2s',
                  '&:hover': {
                    bgcolor: 'error.main',
                    color: 'common.white',
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          </Box>
        </Stack>
        <Divider />

        <Stack
          spacing={1}
          mb={2}
          sx={{
            border: hasErrors.length > 0 ? '2px solid #f44336' : null,
            borderRadius: 1,
            p: hasErrors.length > 0 ? 1 : 0,
            background: hasErrors.length > 0 ? '#fff6f6' : null,
          }}
        >
          {
            hasErrors.length > 0
            && <Typography sx={{ fontSize: '0.8rem' }}>
              The following fields cannot be empty:
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {hasErrors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            </Typography>
          }
          <FieldChipRow
            label="Invoices split by:"
            fieldKey="splitInvoicesBy"
            value={group.splitInvoicesBy ?? ''}
            groupId={group.id!}
            onEdit={onEditField}
          />
          <FieldChipRow
            label="Group lines by:"
            fieldKey="groupLinesBy"
            value={group.groupLinesBy ?? ''}
            groupId={group.id!}
            onEdit={onEditField}
          />
          <FieldChipRow
            label="Line Item:"
            fieldKey="lineItemFields"
            value={group.lineItemFields ?? ''}
            groupId={group.id!}
            onEdit={onEditField}
          />
          <FieldChipRow
            label="Line Description:"
            fieldKey="lineDescriptionFields"
            value={group.lineDescriptionFields ?? ''}
            groupId={group.id!}
            onEdit={onEditField}
          />
        </Stack>

        <Divider sx={{ mb: 1 }} />

        {
          subscriptionLoadState === 'loading'
            ? <Stack direction="row" justifyContent="center" alignItems="center" mt={3}>
              <CircularProgress />
            </Stack>
            : <Box
              sx={{
                maxHeight: 200,
                overflowY: 'auto',
                pr: 1,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {assignedSubscriptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No subscriptions assigned.
                </Typography>
              ) : (
                assignedSubscriptions.map((sub) => (
                  <SubscriptionCard key={sub.id} subscription={sub} selected />
                ))
              )}
            </Box>
        }
      </CardContent>
    </Card>
  );
};

export default InvoiceGroupCard;
